import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Typography, Stack, Divider, Button } from '@mui/material';
import { useRewardCart } from '@src/hooks/contexts';
import styles from '@vp-ui/components/VpRewardsCard.module.less';

const VpRewardsCard = ({ onCheckout }) => {
  const { rewardItems } = useRewardCart();
  
  const totalPrice = rewardItems.reduce((sum, item) => sum + (item.campaignRewardAmount || 0), 0);
  const platformFee = totalPrice * 0.1; // 10% fee
  const grandTotal = totalPrice + platformFee;
  const itemsByCampaign = rewardItems.reduce((acc, item) => {
    const campaignName = item.campaignName || 'Unknown Campaign';
    if (!acc[campaignName]) {
      acc[campaignName] = [];
    }
    acc[campaignName].push(item);
    return acc;
  }, {});

  const campaignCount = Object.keys(itemsByCampaign).length;

  if (rewardItems.length === 0) {
    return (
      <Card className={styles.rewardsCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            No Rewards Selected
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Go to the Media tab and select submissions to reward with gift cards.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={styles.rewardsCard}>
      <CardContent>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Rewards Summary
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {campaignCount === 1 
            ? `Send gift cards to ${rewardItems.length} recipient${rewardItems.length !== 1 ? 's' : ''}`
            : `Send gift cards to ${rewardItems.length} recipient${rewardItems.length !== 1 ? 's' : ''} from ${campaignCount} campaigns`
          }
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2} mb={3}>
          <Typography variant="h6" fontWeight="bold">
            Selected Recipients ({rewardItems.length})
          </Typography>
          
          {Object.entries(itemsByCampaign).map(([campaignName, items]) => (
            <Stack key={campaignName} spacing={1}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold">
                {campaignName} ({items.length})
              </Typography>
              {items.map((item) => (
                <Stack key={item.submissionId} spacing={0.5} pl={2}>
                  <Typography variant="body2">
                    {item.submitterName} - ${item.campaignRewardAmount || 0}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1.5}>
          {rewardItems.map((item) => (
            <Stack key={item.submissionId} direction="row" justifyContent="space-between">
              <Typography variant="body2">
                {item.submitterName} - ${item.campaignRewardAmount || 0} Gift Card
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                ${(item.campaignRewardAmount || 0).toFixed(2)}
              </Typography>
            </Stack>
          ))}
          
          <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
            <Typography variant="body1" fontWeight="medium">
              Subtotal ({rewardItems.length} recipient{rewardItems.length !== 1 ? 's' : ''})
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              ${totalPrice.toFixed(2)}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="textSecondary">
              Platform Fee (10%)
            </Typography>
            <Typography variant="body2" color="textSecondary">
              ${platformFee.toFixed(2)}
            </Typography>
          </Stack>

          <Divider />

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h6" fontWeight="bold">
              Total
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary">
              ${grandTotal.toFixed(2)}
            </Typography>
          </Stack>
        </Stack>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          onClick={onCheckout}
          sx={{ mt: 3 }}
        >
          Proceed to Checkout
        </Button>
      </CardContent>
    </Card>
  );
};

VpRewardsCard.propTypes = {
  onCheckout: PropTypes.func.isRequired,
};

export default VpRewardsCard;



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpRewardsCard.jsx